import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/site/Logo";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Login failed";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

export default function AdminLogin() {
  const { user, loading, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (!loading && user && user.role === "admin") return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Signed in");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy-base text-white grain flex flex-col" data-testid="admin-login-page">
      <div className="p-6 md:p-10">
        <a href="/" data-testid="login-back-home">
          <Logo size={38} />
        </a>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 pb-24">
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md border border-navy-border bg-navy-surface/60 p-10"
          data-testid="admin-login-form"
        >
          <div className="flex items-center gap-4 mb-2">
            <span className="h-px w-8 bg-gold" />
            <span className="overline">Restricted</span>
          </div>
          <h1 className="h-display text-4xl mt-4">Admin sign-in</h1>
          <p className="mt-3 font-mono text-[11px] tracking-[0.2em] uppercase text-white/50">
            Mends Operations Portal
          </p>

          <div className="mt-10 flex flex-col gap-8">
            <div className="flex flex-col gap-2">
              <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="bg-transparent border-b border-navy-border py-3 focus:outline-none focus:border-gold transition-colors"
                data-testid="admin-login-email"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="bg-transparent border-b border-navy-border py-3 focus:outline-none focus:border-gold transition-colors"
                data-testid="admin-login-password"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={busy}
            className="btn-gold mt-12 w-full justify-center disabled:opacity-50"
            data-testid="admin-login-submit"
          >
            <Lock size={12} />
            {busy ? "Signing in…" : "Sign in"}
          </button>

          <a
            href="/"
            className="mt-8 block text-center font-mono text-[10px] tracking-[0.3em] uppercase text-white/40 hover:text-gold transition-colors"
            data-testid="admin-login-back"
          >
            ← Back to site
          </a>
        </motion.form>
      </div>
    </div>
  );
}
